import { useState } from 'react'

import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'

import {
  DEPARTEMENT_LEVEL_MAPPING,
  NON_PNS_LEVEL_MAPPING,
} from 'app/PemetaanKompetensiPage/constants/competency'

import type { JabatanLevelUpperCase } from 'types/competency-profile'

type CompetencyProfileProps = {
  level: JabatanLevelUpperCase
  flow: number
}

type CompetencyTexts = {
  prefixLevel: string
  minimumLevelTexts: {
    description: string
    examples: string[]
    howTo: string
  }
}

const firstFlowTexts = {
  prefixLevel: 'Jenjang Jabatan: Ahli ',
  minimumLevelTexts: {
    description: `Level Minimum adalah standar level sesuai Jenjang Jabatan yang menjadi acuan Anda untuk berefleksi: &nbsp;<strong>“Apakah level saya saat ini sesuai dengan Jenjang Jabatan saya?”</strong>`,
    examples: [
      'Jika Jenjang Jabatan Anda adalah Ahli Pertama, maka Level Minimum untuk setiap kompetensi (Pedagogik, Kepribadian, Sosial, dan Profesional) adalah 2.',
      'Begitu pun dengan Jenjang Jabatan lainnya. Untuk Ahli Muda: 3,Ahli Madya: 4, dan Ahli Utama: 5.',
    ],
    howTo:
      'Bagaimana jika level saat ini belum mencapai Level Minimum? Jangan khawatir, Anda bisa ikuti pembelajaran yang kami sediakan dan perbarui level Anda di periode refleksi berikutnya.',
  },
}

const secondFlowTexts = {
  prefixLevel: 'Tahun Mengajar: ',
  minimumLevelTexts: {
    description: `Level Minimum adalah standar level sesuai Tahun Mengajar (dihitung dari TMT Anda di SK terakhir yang terbit) yang menjadi acuan Anda untuk berefleksi: <strong>“Apakah level saya saat ini sesuai dengan tahun mengajar saya?”</strong>`,
    examples: [
      'Jika Tahun Mengajar Anda adalah 0-7 tahun, maka Level Minimum untuk setiap kompetensi (Pedagogik, Kepribadian, Sosial, dan Profesional) adalah 2.',
      `Begitu pun dengan Tahun Mengajar lainnya: <ul class="!list-disc list-outside pl-4"><li>Untuk 8-14 tahun, Level Minimumnya 3.</li><li>Untuk 15-20 tahun, Level Minimumnya 4.</li><li>Untuk lebih dari 21 tahun, Level Minimumnya 5.</li></ul>`,
    ],
    howTo:
      'Bagaimana jika level saat ini belum mencapai Level Minimum? Jangan khawatir, Anda bisa ikuti rekomendasi belajar yang kami sediakan dan perbarui level Anda di periode refleksi berikutnya.',
  },
}

export function CompetencyProfileCard({ level, flow }: CompetencyProfileProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = () => setIsOpen(false)

  const competencyTexts: CompetencyTexts =
    flow === 2 ? secondFlowTexts : firstFlowTexts

  return (
    <>
      <button
        className="bg-white mt-3 p-3 rounded-lg flex justify-between items-center text-left w-full border border-subdued"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-col gap-1">
          <Text variant="body-sm" className="capitalize">
            {competencyTexts.prefixLevel}
            {flow === 2 ? NON_PNS_LEVEL_MAPPING[level] : level.toLowerCase()}
          </Text>
          <Text variant="body-sm">
            Level minimum:
            <Text
              as="span"
              variant="helper-bold"
              color="inverse"
              className="ml-1 inline-flex h-5 w-5 bg-primary rounded-full justify-center items-center text-white"
            >
              {DEPARTEMENT_LEVEL_MAPPING[level]}
            </Text>
          </Text>
        </div>
        <Icon>chevron_right</Icon>
      </button>
      <Dialog isOpen={isOpen} onClose={handleClose} size="sm">
        <DialogOverlay />
        <DialogContent className="p-4">
          <div className="space-y-3">
            <Text variant="heading-md">Apa itu Level Minimum?</Text>
            <Text
              dangerouslySetInnerHTML={{
                __html: competencyTexts.minimumLevelTexts.description,
              }}
            />
            <div>
              <Text>Contoh:</Text>
              <ul className="list-disc list-outside pl-4">
                {competencyTexts.minimumLevelTexts.examples.map(
                  (thisExample, index) => (
                    <Text
                      as="li"
                      key={`example_${index}`}
                      dangerouslySetInnerHTML={{ __html: thisExample }}
                    />
                  )
                )}
              </ul>
            </div>
            <Text>{competencyTexts.minimumLevelTexts.howTo}</Text>
          </div>

          <DialogCancel
            as={Button}
            onClick={handleClose}
            fullWidth
            className="mt-4"
          >
            Saya mengerti
          </DialogCancel>
        </DialogContent>
      </Dialog>
    </>
  )
}
